import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--unlock-key"
//
// CONEK-3018: Gates editing the locked technical `key` of a reference data
// entity from an admin edit form. The key field renders read-only with a lock
// badge and an "Unlock to edit" button. Clicking the button opens a warning
// modal (Ui::UnlockKeyModalComponent) that requires a free-text reason. On
// confirm, the key input becomes editable and two hidden fields are populated
// (`unlock_key=true`, `unlock_reason=<reason>`) so the interactor wraps the
// update in PackSupport::KeyUnlock for the forensic audit trail.
//
// Required targets:
//   modal        — the <dialog> rendered with the ui--modal controller
//   reason       — the textarea inside the modal
//   keyInput     — the <input> holding the key (rendered readonly)
//   unlockFlag   — hidden <input name="unlock_key"> (initial value "false")
//   unlockReason — hidden <input name="unlock_reason">
//
// Optional targets:
//   reasonError  — element shown when the reason is left blank
//   unlockButton — the "Unlock to edit" button (hidden once unlocked)
//   lockBadge    — the 🔒 Locked badge (hidden once unlocked)
export default class extends Controller {
  static targets = [
    "modal",
    "reason",
    "reasonError",
    "keyInput",
    "unlockFlag",
    "unlockReason",
    "unlockButton",
    "lockBadge",
  ]

  // Opens the warning modal (wired to the "Unlock to edit" button).
  open() {
    this._clearError()
    if (this.hasReasonTarget) this.reasonTarget.value = ""
    this._openModal()
  }

  // Validates the reason, then unlocks the key field and stamps the hidden
  // fields the interactor reads.
  confirm() {
    const reason = this.hasReasonTarget ? this.reasonTarget.value.trim() : ""
    if (!reason) {
      this._showError()
      return
    }

    if (this.hasUnlockFlagTarget) this.unlockFlagTarget.value = "true"
    if (this.hasUnlockReasonTarget) this.unlockReasonTarget.value = reason

    if (this.hasKeyInputTarget) {
      this.keyInputTarget.readOnly = false
      this.keyInputTarget.removeAttribute("disabled")
      this.keyInputTarget.classList.remove("cursor-not-allowed", "opacity-60")
    }
    if (this.hasLockBadgeTarget) this.lockBadgeTarget.classList.add("hidden")
    if (this.hasUnlockButtonTarget)
      this.unlockButtonTarget.classList.add("hidden")

    this._closeModal()
    if (this.hasKeyInputTarget) this.keyInputTarget.focus()
  }

  cancel() {
    this._closeModal()
  }

  clearError() {
    this._clearError()
  }

  // --- Internal helpers ---

  _showError() {
    if (this.hasReasonErrorTarget)
      this.reasonErrorTarget.classList.remove("hidden")
  }

  _clearError() {
    if (this.hasReasonErrorTarget)
      this.reasonErrorTarget.classList.add("hidden")
  }

  _openModal() {
    if (!this.hasModalTarget) return
    const controller = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
    if (controller) {
      controller.open()
    } else if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    }
  }

  _closeModal() {
    if (!this.hasModalTarget) return
    const controller = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
    if (controller) {
      controller.close()
    } else if (this.modalTarget.open) {
      this.modalTarget.close()
    }
  }
}
